/**
 * Render-time URL scheme-allowlist guard for the public player (WEB-173 F9).
 *
 * Defense-in-depth mirror of the backend write-path policy
 * (backend/src/ai-funnels/block-schema/url-allowlist.ts). The write guard stops
 * new bad data, but the player executes window.location / renders href itself,
 * and old records (pre-F9) may still hold a javascript: url — so the player
 * must guard at render too (packet anti-pattern 3, AC "write AND render").
 *
 * Policy: http/https/mailto + scheme-less (relative/anchor) pass; everything
 * with a disallowed scheme (javascript:/data:/vbscript:/file:/...) or a `{{var}}`
 * interpolation (D7) is rejected.
 *
 * WEB-1906 — единственное исключение: служебный маркер `w2a://store` («адрес
 * магазина подставит рантайм по платформе посетителя», см. utils/storeAutoUrl).
 * Разрешено ТОЧНОЕ значение, а не схема `w2a` целиком: иначе вместе с маркером
 * проехал бы любой `w2a://…`, а маркер — признак, а не адрес, и множество его
 * допустимых значений состоит ровно из одного элемента. Плеер подменяет маркер
 * настоящим адресом ДО safeHref, так что в норме сторож его не видит; правило
 * стоит здесь на случай, когда маркер доехал до другой ветки рендера — там он
 * должен схлопнуться в `#`, а не увести покупателя по несуществующей схеме.
 */
import { STORE_AUTO_URL } from './storeAutoUrl';

const ALLOWED_SCHEMES: ReadonlySet<string> = new Set([
  'http',
  'https',
  'mailto',
]);

const SCHEME_RE = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;

function isIgnorableChar(code: number): boolean {
  return (
    code <= 0x20 || // C0 controls + space
    (code >= 0x7f && code <= 0x9f) || // DEL + C1 controls
    code === 0xa0 || // NBSP
    code === 0x180e || // Mongolian vowel separator
    (code >= 0x2000 && code <= 0x200f) || // unicode spaces + zero-width + LRM/RLM
    (code >= 0x2028 && code <= 0x202f) || // line/para sep + bidi + narrow NBSP
    code === 0x205f || // medium math space
    code === 0x2060 || // word joiner
    code === 0xfeff // BOM / zero-width no-break space
  );
}

/**
 * WEB-1911: очистка экспортируется, чтобы соседняя дверь (`appLinkHref` —
 * своя проверка адреса у кнопок «в приложение») чистила значение ТОЙ ЖЕ
 * функцией. Вторая копия этой чистки — ровно тот дефект, который уже ловили
 * в safeHref ревизией пачки 18: `w2a:/<zero-width>/store` проходил мимо
 * точного сравнения, потому что две двери одного правила чистили по-разному.
 * Политика разрешённых схем при этом НЕ расширяется.
 */
export function stripControlAndSpace(value: string): string {
  let out = '';
  for (const ch of value) {
    if (!isIgnorableChar(ch.charCodeAt(0))) {
      out += ch;
    }
  }
  return out;
}

export function isAllowedUrl(raw: unknown): boolean {
  if (typeof raw !== 'string') {
    return false;
  }
  if (raw.includes('{{') || raw.includes('}}')) {
    return false;
  }
  const cleaned = stripControlAndSpace(raw);
  if (cleaned.length === 0) {
    return true;
  }
  // WEB-1906: сверяем ОЧИЩЕННОЕ значение — иначе `w2a:/<zero-width>/store`
  // прошёл бы мимо точного сравнения ровно так же, как обходят allowlist схем.
  if (cleaned === STORE_AUTO_URL) {
    return true;
  }
  const match = SCHEME_RE.exec(cleaned);
  if (!match) {
    return true;
  }
  return ALLOWED_SCHEMES.has(match[1].toLowerCase());
}

/**
 * Returns the CLEANED url when `raw` is a non-empty allowed url, otherwise
 * `fallback`. Use for href attributes (replaces the bare `block.url || '#'`
 * pattern).
 *
 * НАРУЖУ УХОДИТ ТО, ЧТО ПРОВЕРЕНО (ревизия пачки 19). Проверка идёт по
 * очищенной строке (иначе невидимые символы обходят список схем — шрам
 * WEB-1906), а возвращалось СЫРОЕ значение: проверили один адрес, в разметку
 * поставили другой — `https://exa<zero-width>mple.com` доезжал до `href` с
 * невидимыми символами внутри хоста, и куда он ведёт, решал уже браузер. Та
 * же одна строка на обе роли, что и у соседней двери `appLinkHref`
 * (utils/appLinkHref.ts), — иначе двери одного правила снова разъедутся.
 */
export function safeHref(
  raw: string | undefined | null,
  fallback = '#',
): string {
  if (typeof raw !== 'string' || raw.length === 0 || !isAllowedUrl(raw)) {
    return fallback;
  }
  // WEB-1906 (приёмка пачки 18): маркер `w2a://store` проходит isAllowedUrl
  // (это допустимое ЗНАЧЕНИЕ поля), но href'ом быть не может — это признак,
  // а не адрес. Единственная ветка, которая умеет его разрешить, — button_link
  // в PublicBlockRenderer, и она подменяет маркер настоящим адресом ДО
  // safeHref. Везде, где маркер доехал сюда как есть (redirect_url, ссылка у
  // картинки, чужая ветка), он обязан схлопнуться в запасное значение, а не
  // уйти покупателю мёртвым `href="w2a://store"`.
  //
  // Ревизия пачки 18: чистим ТОЙ ЖЕ функцией, что и isAllowedUrl выше. Пока
  // здесь стоял голый `raw.trim()`, две двери одного правила расходились:
  // `w2a://sto\u200bre` allowlist пропускал (он сверяет очищенную строку), а
  // эта проверка — нет, и мёртвый адрес уезжал покупателю в href.
  const cleaned = stripControlAndSpace(raw);
  if (cleaned === STORE_AUTO_URL) {
    return fallback;
  }
  // Строка из одних невидимых символов адресом не является: пустой href
  // ведёт на текущую страницу — это не то, что задумал автор. Раньше сюда
  // уходило сырое «   », теперь очищенное схлопнулось бы в пустоту.
  if (cleaned.length === 0) {
    return fallback;
  }
  // Свежий взгляд по пачке 19 (проход 3): наружу уходит адрес БЕЗ невидимых
  // символов, но С пробелами. Полная чистка нужна только для распознавания
  // схемы; применённая ко всей строке, она склеивала текст в query —
  // `wa.me/…?text=Здравствуйте, я с квиза` уезжал покупателю как
  // `…text=Здравствуйте,ясквиза`. Пробелы браузер кодирует сам, а вот
  // zero-width внутри хоста решать ему нельзя.
  return stripInvisibleOnly(raw).trim();
}

/**
 * Невидимые символы, которые не являются пробелом: управляющие, zero-width,
 * bidi-метки, BOM. Обычные пробелы (0x20, NBSP, юникод-пробелы) остаются —
 * они часть текста параметров адреса и кодируются браузером.
 */
function isInvisibleNonSpace(code: number): boolean {
  return (
    code < 0x20 || // C0 controls (без пробела 0x20)
    (code >= 0x7f && code <= 0x9f) || // DEL + C1 controls
    code === 0x180e || // Mongolian vowel separator
    (code >= 0x200b && code <= 0x200f) || // zero-width + LRM/RLM
    (code >= 0x2028 && code <= 0x202e) || // line/para sep + bidi (не 0x202f — узкий NBSP)
    code === 0x2060 || // word joiner
    code === 0xfeff // BOM / zero-width no-break space
  );
}

export function stripInvisibleOnly(value: string): string {
  let out = '';
  for (const ch of value) {
    if (!isInvisibleNonSpace(ch.charCodeAt(0))) {
      out += ch;
    }
  }
  return out;
}
